// "use client";

import React from "react";
import { Card, Typography, Form as AntForm, Input, Button, Space } from "antd";
import { MailOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import { Formik } from "formik";

const { Title, Text } = Typography;

export type RegisterCardProps = {
    onSubmit: (values: { name: string; email: string; password: string }) => Promise<void> | void;
    loading?: boolean;

    /** Логотип: передай <BrandLogo ... /> */
    logo?: React.ReactNode;
    /** Заголовок, по умолчанию "Create Account" */
    title?: React.ReactNode;

    /** Значения по умолчанию */
    initialName?: string;
    initialEmail?: string;

    /** Ссылка "Sign in" */
    signInHref?: string;
    onSignInClick?: () => void;

    /** Внешний контейнер */
    className?: string;
    style?: React.CSSProperties;
};

export function RegisterCard({
    onSubmit,
    loading,
    logo,
    title = "Создать аккаунт",
    initialName = "",
    initialEmail = "",
    signInHref,
    onSignInClick,
    className,
    style,
}: RegisterCardProps) {
    const validate = (values: { name: string; email: string; password: string }) => {
        const errors: Partial<typeof values> = {};
        if (!values.name || values.name.trim().length < 2)
            errors.name = "Введите имя (минимум 2 символа)";
        if (!values.email) errors.email = "Введите e-mail";
        else if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = "Некорректный e-mail";

        if (!values.password) errors.password = "Введите пароль";
        else if (values.password.length < 6) errors.password = "Минимум 6 символов";
        return errors;
    };

    return (
        <Card
            className={className}
            style={{
                maxWidth: 460,
                margin: "0 auto",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                ...style,
            }}
            styles={{ body: { padding: 32 } }}
        >
            <Space direction="vertical" align="center" style={{ width: "100%" }} size={24}>
                {logo}
                <Title level={2} style={{ margin: 0, textAlign: "center", lineHeight: 1.1 }}>
                    {title}
                </Title>

                <Formik
                    initialValues={{
                        name: initialName,
                        email: initialEmail,
                        password: "",
                    }}
                    validate={validate}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await onSubmit(values);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        values,
                        errors,
                        touched,
                        isSubmitting,
                    }) => (
                        <AntForm
                            layout="vertical"
                            onFinish={handleSubmit}
                            style={{ width: "100%", marginTop: 8 }}
                        >
                            <AntForm.Item
                                label="Ваше имя"
                                validateStatus={touched.name && errors.name ? "error" : undefined}
                                help={touched.name && errors.name ? errors.name : undefined}
                            >
                                <Input
                                    name="name"
                                    size="large"
                                    placeholder="Имя"
                                    prefix={<UserOutlined />}
                                    value={values.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    autoComplete="name"
                                    allowClear
                                    style={{ borderRadius: 9999 }}
                                />
                            </AntForm.Item>

                            <AntForm.Item
                                label="Электронная почта"
                                validateStatus={touched.email && errors.email ? "error" : undefined}
                                help={touched.email && errors.email ? errors.email : undefined}
                            >
                                <Input
                                    name="email"
                                    type="email"
                                    size="large"
                                    placeholder="e-mail"
                                    prefix={<MailOutlined />}
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    autoComplete="email"
                                    allowClear
                                    style={{ borderRadius: 9999 }}
                                />
                            </AntForm.Item>

                            <AntForm.Item
                                label="Пароль"
                                validateStatus={
                                    touched.password && errors.password ? "error" : undefined
                                }
                                help={
                                    touched.password && errors.password
                                        ? errors.password
                                        : undefined
                                }
                            >
                                <Input.Password
                                    name="password"
                                    size="large"
                                    placeholder="пароль"
                                    prefix={<LockOutlined />}
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    autoComplete="new-password"
                                    onPressEnter={(e) => {
                                        e.currentTarget.blur();
                                        handleSubmit();
                                    }}
                                    style={{ borderRadius: 9999 }}
                                />
                            </AntForm.Item>

                            <Button
                                htmlType="submit"
                                type="primary"
                                size="large"
                                block
                                loading={loading || isSubmitting}
                                style={{ borderRadius: 9999, height: 44 }}
                            >
                                Создать аккаунт
                            </Button>

                            <Space
                                direction="vertical"
                                align="center"
                                style={{ width: "100%", marginTop: 16 }}
                                size={6}
                            >
                                <Text type="secondary">
                                    У вас уже есть учетная запись?{" "}
                                    <a
                                        href={signInHref}
                                        onClick={(e) => {
                                            if (onSignInClick) {
                                                e.preventDefault();
                                                onSignInClick();
                                            }
                                        }}
                                    >
                                        Вход
                                    </a>
                                </Text>
                            </Space>
                        </AntForm>
                    )}
                </Formik>
            </Space>
        </Card>
    );
}
